import { api } from "@lib/orpc-client"
import { launchWebSdk } from "@lib/kyc"
import { useEffect, useState, useRef } from "react"
import * as Sentry from "@sentry/react"
import { useTranslation } from "react-i18next"

const useLaunchSumsubSDK = ({
    levelName,
    onComplete,
    onPending,
    onError,
}: {
    levelName: string
    onComplete: () => Promise<void>
    onPending: () => Promise<void>
    onError: (error: any) => Promise<void>
}) => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const sdkLaunchedRef = useRef(false)

    const resetSdk = () => {
        sdkLaunchedRef.current = false
    }

    const getAccessToken = async (levelName: string, retryCount = 0, maxRetries = 3) => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.walletV2.kyc.getAccessToken({ levelName })
            setLoading(false)
            if (!response.token) {
                throw new Error('Invalid token')
            }
            return response.token
        } catch (error) {
            if (retryCount < maxRetries) {
                return getAccessToken(levelName, retryCount + 1, maxRetries)
            } else {
                Sentry.captureException(error)
                setError(t('components.sumsubKYC.errorMessage'))
                setLoading(false)
                return null
            }
        }
    }

    useEffect(() => {
        if (!levelName || sdkLaunchedRef.current) return

        const initializeSDK = async () => {
            const initialToken = await getAccessToken(levelName)
            if (initialToken) {
                sdkLaunchedRef.current = true
                launchWebSdk({
                    token: initialToken,
                    onComplete,
                    onPending,
                    onError: async (error) => {
                        sdkLaunchedRef.current = false
                        await onError(error)
                    },
                    onGetAccessToken: async () => getAccessToken(levelName)
                })
            } else {
                sdkLaunchedRef.current = false
            }
        }

        initializeSDK()

        return () => {
            sdkLaunchedRef.current = false
        }
    }, [levelName])

    return {
        loading,
        error,
        resetSdk
    }
}

export default useLaunchSumsubSDK
